import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  X,
  Layers,
  Archive,
  Plus,
  Briefcase,
  User as UserIcon,
  Pencil,
  Trash2,
  MoreHorizontal,
  Check,
  GripVertical,
  Settings as SettingsIcon,
  LogOut,
  Zap,
  FileText,
  CreditCard,
  Tag,
  HelpCircle,
  BookOpen,
  Home as HomeIcon,
  ExternalLink,
  Share2 as Share2Icon,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/**
 * MobileMenu — slide-in drawer exposing the full app navigation on small screens.
 * Renders the same filter/list management surface as <Sidebar> plus the Header
 * actions (Settings, Sign out, Admin) and marketing links (Pricing, Help, Blog).
 */
export default function MobileMenu({
  open,
  onClose,
  user,
  billing,
  filter,
  setFilter,
  counts,
  lists,
  onCreateList,
  onUpdateList,
  onDeleteList,
  onReorderLists,
  onShareList,
  onShareWorkspace,
  onOpenSettings,
  onLogout,
}) {
  const [renamingId, setRenamingId] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [menuOpenId, setMenuOpenId] = useState(null);

  const ownedLists = lists.filter((l) => l.share_origin === "owner" || !l.share_origin);
  const sharedLists = lists.filter((l) => l.share_origin && l.share_origin !== "owner");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // Auto-close menu on filter selection (better UX)
  const pickFilter = (key) => {
    setFilter(key);
    onClose();
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ownedLists.findIndex((l) => l.id === active.id);
    const newIndex = ownedLists.findIndex((l) => l.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorderLists(arrayMove(ownedLists, oldIndex, newIndex).map((l) => l.id));
  };

  if (!open) return null;

  const planLabel = billing?.grandfathered
    ? "Pro · Lifetime"
    : billing?.plan === "trialing"
      ? `Trial · ${billing.days_remaining}d left`
      : billing?.plan === "pro"
        ? `Pro · ${billing.days_remaining ?? "—"}d left`
        : `Free · ${billing?.list_count ?? 0}/${billing?.list_limit ?? 2} lists`;

  return (
    <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-menu">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden
        data-testid="mobile-menu-backdrop"
      />
      {/* Drawer */}
      <div className="absolute left-0 top-0 bottom-0 w-[88vw] max-w-sm bg-white shadow-2xl overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 shrink-0">
          <div className="text-base font-semibold tracking-tight">Menu</div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
            aria-label="Close menu"
            data-testid="mobile-menu-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User + plan */}
        {user && (
          <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3" data-testid="mobile-menu-user">
            {user.picture ? (
              <img src={user.picture} alt={user.name || user.email} className="w-10 h-10 border border-zinc-300 object-cover" />
            ) : (
              <div className="w-10 h-10 bg-zinc-100 border border-zinc-300 flex items-center justify-center text-sm font-semibold text-zinc-700">
                {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-zinc-900 truncate">{user.name || "—"}</p>
              <p className="text-xs text-zinc-500 truncate">{user.email}</p>
            </div>
          </div>
        )}

        {/* Plan / billing row */}
        <Link
          to="/billing"
          onClick={onClose}
          className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 hover:bg-zinc-50 transition-colors"
          data-testid="mobile-menu-billing"
        >
          <span className="flex items-center gap-3 text-sm text-zinc-700">
            <CreditCard className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
            {planLabel}
          </span>
          {billing && !billing.grandfathered && billing.plan === "free" && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-[0.12em] px-2 py-1 bg-[#002FA7] text-white">
              <Zap className="w-3 h-3" /> Upgrade
            </span>
          )}
        </Link>

        {/* Filters */}
        <div className="px-5 pt-5 pb-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-2">Views</p>
          <div className="space-y-1">
            {[{ key: "all", label: "All", Icon: Layers }, { key: "completed", label: "Completed", Icon: Archive }].map(({ key, label, Icon }) => {
              const active = filter === key;
              const isArchive = key === "completed";
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => pickFilter(key)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 border transition-colors ${
                    active
                      ? isArchive
                        ? "border-emerald-700 bg-emerald-700 text-white"
                        : "border-zinc-900 bg-zinc-900 text-white"
                      : "border-transparent text-zinc-700 hover:bg-zinc-50 hover:border-zinc-200"
                  }`}
                  data-testid={`mobile-filter-${key}`}
                >
                  <span className="flex items-center gap-2.5">
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                    <span className="text-sm font-medium">{label}</span>
                  </span>
                  <span className={`text-xs font-mono ${active ? "text-white/70" : "text-zinc-400"}`}>{counts[key] ?? 0}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Lists */}
        <div className="px-5 pt-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Lists</p>
            <button
              type="button"
              onClick={() => { onCreateList(); onClose(); }}
              className="text-zinc-500 hover:text-zinc-900 p-1 -mr-1"
              aria-label="New list"
              data-testid="mobile-new-list-btn"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-1">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={ownedLists.map((l) => l.id)} strategy={verticalListSortingStrategy}>
                {ownedLists.map((list) => (
                  <MobileListRow
                    key={list.id}
                    list={list}
                    active={filter === `list:${list.id}`}
                    count={counts[`list:${list.id}`] ?? 0}
                    isRenaming={renamingId === list.id}
                    draftName={draftName}
                    setDraftName={setDraftName}
                    onStartRename={() => { setRenamingId(list.id); setDraftName(list.name); setMenuOpenId(null); }}
                    onCommitRename={async () => {
                      const id = renamingId; const next = draftName.trim();
                      setRenamingId(null);
                      if (!id || !next || next === list.name) return;
                      await onUpdateList(id, { name: next });
                    }}
                    onCancelRename={() => { setRenamingId(null); setDraftName(""); }}
                    menuOpen={menuOpenId === list.id}
                    setMenuOpenId={setMenuOpenId}
                    onSelect={() => pickFilter(`list:${list.id}`)}
                    onUpdateList={onUpdateList}
                    onDeleteList={(l) => { onDeleteList(l); onClose(); }}
                    onShareList={onShareList ? (l) => { onClose(); onShareList(l); } : null}
                  />
                ))}
              </SortableContext>
            </DndContext>
            <button
              type="button"
              onClick={() => { onCreateList(); onClose(); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.1em] font-medium text-zinc-500 hover:text-zinc-900 border border-dashed border-zinc-300 hover:border-zinc-900 transition-colors"
              data-testid="mobile-new-list-cta"
            >
              <Plus className="w-3.5 h-3.5" /> New list
            </button>
            {onShareWorkspace && (
              <button
                type="button"
                onClick={() => { onClose(); onShareWorkspace(); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.12em] font-medium text-zinc-400 hover:text-[#002FA7] transition-colors"
                data-testid="mobile-share-workspace-btn"
              >
                Share whole workspace
              </button>
            )}
          </div>
        </div>

        {sharedLists.length > 0 && (
          <div className="px-5 pt-6" data-testid="mobile-shared-with-me">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-2">Shared with me</p>
            <div className="space-y-1">
              {sharedLists.map((list) => {
                const active = filter === `list:${list.id}`;
                const CatIcon = list.category === "personal" ? UserIcon : Briefcase;
                return (
                  <button
                    key={list.id}
                    type="button"
                    onClick={() => pickFilter(`list:${list.id}`)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left border transition-colors ${active ? "border-emerald-700 bg-emerald-700 text-white" : "border-transparent hover:bg-zinc-50 hover:border-zinc-200"}`}
                    data-testid={`mobile-shared-list-${list.id}`}
                  >
                    <CatIcon className={`w-4 h-4 shrink-0 ${active ? "text-white/80" : "text-zinc-500"}`} strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${active ? "text-white" : "text-zinc-700"}`}>{list.name}</div>
                      <div className={`text-[10px] truncate ${active ? "text-white/70" : "text-zinc-500"}`}>
                        from {list.owner_name || list.owner_email} · {list.permission}
                      </div>
                    </div>
                    <span className={`text-xs font-mono ${active ? "text-white/70" : "text-zinc-400"}`}>{counts[`list:${list.id}`] ?? 0}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Account actions */}
        <div className="px-5 pt-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-2">Account</p>
          <div className="space-y-1">
            <button
              type="button"
              onClick={() => { onOpenSettings(); onClose(); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors"
              data-testid="mobile-menu-settings"
            >
              <SettingsIcon className="w-4 h-4 text-zinc-500" strokeWidth={1.5} /> Settings
            </button>
            {user?.role === "admin" && (
              <Link
                to="/admin/blog"
                onClick={onClose}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors"
                data-testid="mobile-menu-admin-blog"
              >
                <FileText className="w-4 h-4 text-zinc-500" strokeWidth={1.5} /> Admin · Blog
              </Link>
            )}
            <button
              type="button"
              onClick={() => { onClose(); onLogout(); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-700 hover:bg-red-50 transition-colors"
              data-testid="mobile-menu-logout"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
            </button>
          </div>
        </div>

        {/* Marketing links */}
        <div className="px-5 pt-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-2">Explore</p>
          <div className="space-y-1">
            {[
              { to: "/", label: "Home", Icon: HomeIcon },
              { to: "/pricing", label: "Pricing", Icon: Tag },
              { to: "/help", label: "Help", Icon: HelpCircle },
              { to: "/blog", label: "Blog", Icon: BookOpen },
              { to: "/changelog", label: "Changelog", Icon: FileText },
            ].map(({ to, label, Icon }) => (
              <Link
                key={to}
                to={to}
                onClick={onClose}
                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-50 transition-colors"
                data-testid={`mobile-menu-link-${label.toLowerCase()}`}
              >
                <Icon className="w-4 h-4 text-zinc-400" strokeWidth={1.5} /> {label}
              </Link>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto px-5 py-5 border-t border-zinc-200">
          <a
            href="https://charlottebryan.com.au/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-500 hover:text-zinc-900 transition-colors"
            data-testid="mobile-menu-charlotte"
          >
            A <span className="font-semibold text-zinc-900">Charlotte Bryan</span> app <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
}

function MobileListRow({
  list, active, count, isRenaming, draftName, setDraftName,
  onStartRename, onCommitRename, onCancelRename,
  menuOpen, setMenuOpenId, onSelect, onUpdateList, onDeleteList, onShareList,
}) {
  const CatIcon = list.category === "personal" ? UserIcon : Briefcase;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: list.id, disabled: isRenaming,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : "auto",
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative border ${
        active ? "border-zinc-900 bg-zinc-900 text-white" : "border-transparent hover:border-zinc-200"
      } ${isDragging ? "shadow-lg" : ""}`}
      data-testid={`mobile-list-row-${list.id}`}
    >
      {isRenaming ? (
        <div className="flex items-center gap-1 px-2.5 py-2">
          <CatIcon className="w-4 h-4 text-zinc-500 shrink-0" strokeWidth={1.5} />
          <input
            autoFocus
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onCommitRename();
              if (e.key === "Escape") onCancelRename();
            }}
            onBlur={onCommitRename}
            maxLength={60}
            className="flex-1 min-w-0 text-sm bg-transparent outline-none border-b border-zinc-400 focus:border-zinc-900 text-zinc-900"
            data-testid={`mobile-list-rename-input-${list.id}`}
          />
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onCommitRename} className="p-1 text-emerald-600">
            <Check className="w-4 h-4" />
          </button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onCancelRename} className="p-1 text-zinc-500">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-stretch">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className={`px-2 cursor-grab active:cursor-grabbing ${active ? "text-white/60" : "text-zinc-400"}`}
            aria-label={`Drag ${list.name}`}
            data-testid={`mobile-list-drag-${list.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onSelect}
            className="flex-1 flex items-center gap-2.5 py-2.5 pr-1 text-left min-w-0"
            data-testid={`mobile-list-select-${list.id}`}
          >
            <CatIcon className={`w-4 h-4 shrink-0 ${active ? "text-white/80" : "text-zinc-500"}`} strokeWidth={1.5} />
            <span className={`text-sm font-medium flex-1 truncate ${active ? "text-white" : "text-zinc-700"}`}>{list.name}</span>
            <span className={`text-xs font-mono ${active ? "text-white/70" : "text-zinc-400"}`}>{count}</span>
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpen ? null : list.id); }}
            className={`px-2 ${active ? "text-white" : "text-zinc-500"}`}
            data-testid={`mobile-list-menu-btn-${list.id}`}
            aria-label="List options"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
        </div>
      )}

      {!isRenaming && menuOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setMenuOpenId(null)} />
          <div className="absolute right-1 top-10 z-20 bg-white border border-zinc-200 shadow-lg w-44 py-1" data-testid={`mobile-list-menu-${list.id}`}>
            <button type="button" onClick={onStartRename} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50 text-left">
              <Pencil className="w-3 h-3" /> Rename
            </button>
            {onShareList && (
              <button
                type="button"
                onClick={() => { setMenuOpenId(null); onShareList(list); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50 text-left"
                data-testid={`mobile-list-share-${list.id}`}
              >
                <Share2Icon className="w-3 h-3" /> Share list…
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setMenuOpenId(null);
                const next = list.category === "work" ? "personal" : "work";
                if (window.confirm(`Change category to ${next}? All tasks in this list will be re-tagged as ${next}.`)) {
                  onUpdateList(list.id, { category: next });
                }
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50 text-left"
            >
              {list.category === "work" ? <UserIcon className="w-3 h-3" /> : <Briefcase className="w-3 h-3" />}
              Move to {list.category === "work" ? "Personal" : "Work"}
            </button>
            {!list.is_default && (
              <button
                type="button"
                onClick={() => { setMenuOpenId(null); onDeleteList(list); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-700 hover:bg-red-50 text-left"
              >
                <Trash2 className="w-3 h-3" /> Delete list…
              </button>
            )}
            {list.is_default && (
              <div className="px-3 py-1.5 text-[10px] text-zinc-400 italic">Default · can't be deleted</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
